import { AlertTriangleIcon, CheckCircle2Icon, TriangleAlertIcon } from 'lucide-react';

import type { CellCoverage, ModuleReadiness } from '@/lib/api';
import { useLabel, useT, useTn } from '@/lib/i18n';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';

/**
 * The readiness report — what a module change would leave behind.
 *
 * Shown against a change that has NOT been saved (`plan-beta` §0b: "shown
 * *before* the change is saved, alongside the config diff, so the consequence is
 * visible while it is still a decision"). It is deliberately not a blocker: an
 * operator narrowing a module's item types usually means it, and a platform that
 * refuses would send them to the database. It is a statement of cost.
 *
 * Two numbers, and they are not interchangeable:
 *
 *   - **stranded** items stop being scoreable by this module. A stranded draft
 *     is an authoring problem; a stranded *certified* item is a coverage one,
 *     because it was in a sampling pool an hour ago.
 *   - **newlyWarning** cells were green before this change and are amber after.
 *     Those are the ones that will refuse to generate for a persona, and they
 *     are the reason to reconsider rather than merely to know.
 */

function CoverageLine({ cell }: { cell: CellCoverage }) {
  const t = useT();
  return (
    <li className="flex items-center justify-between gap-3 font-mono text-xs">
      <span>{cell.personaId}</span>
      <span className="text-muted-foreground">
        {t('readiness.availableNeeded', {
          available: cell.effectivePoolSize,
          needed: cell.itemCount,
        })}
      </span>
    </li>
  );
}

export function ReadinessReport({
  readiness,
  /** Suppresses the all-clear line where the surrounding dialog already says it. */
  quietWhenClean = false,
}: {
  readiness: ModuleReadiness;
  quietWhenClean?: boolean;
}) {
  const t = useT();
  const tn = useTn();
  // `readiness.reason.*` — the two ways an item can be stranded, a closed set
  // fixed by the server's `StrandedItem['reason']`.
  const label = useLabel();
  const { stranded, strandedCertified, newlyWarning, coverage } = readiness;
  const amber = coverage.filter((c) => c.status === 'warning');
  const clean = stranded.length === 0 && newlyWarning.length === 0;

  if (clean && quietWhenClean) return null;

  if (clean) {
    return (
      <Alert>
        <CheckCircle2Icon className="size-4" />
        <AlertDescription>
          {readiness.itemsChecked === 0
            ? t('readiness.noItems')
            : tn('readiness.allFit', readiness.itemsChecked)}
          {amber.length > 0 && ` ${tn('readiness.alreadyAmber', amber.length)}`}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-3">
      {newlyWarning.length > 0 && (
        <Alert variant="destructive" role="alert">
          <TriangleAlertIcon className="size-4" />
          <AlertDescription>
            <p className="mb-1 font-medium">{tn('readiness.goAmber', newlyWarning.length)}</p>
            <ul className="space-y-0.5">
              {newlyWarning.map((cell) => (
                <CoverageLine key={`${cell.personaId}-${cell.moduleId}`} cell={cell} />
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {stranded.length > 0 && (
        <Alert>
          <AlertTriangleIcon className="size-4" />
          <AlertDescription>
            <p className="mb-1 font-medium">
              {t('readiness.stranded', {
                stranded: stranded.length,
                checked: readiness.itemsChecked,
              })}
              {strandedCertified > 0 && ` ${tn('readiness.strandedCertified', strandedCertified)}`}
            </p>
            <p className="text-muted-foreground mb-2 text-xs">{t('readiness.nothingDeleted')}</p>
            <ul className="max-h-40 space-y-0.5 overflow-y-auto">
              {stranded.slice(0, 30).map((item) => (
                <li key={item.itemId} className="flex items-center gap-2 font-mono text-xs">
                  <span className="truncate">{item.itemId}</span>
                  <Badge variant="outline" className="shrink-0">
                    {label('status', item.status)}
                  </Badge>
                  <span className="text-muted-foreground shrink-0">
                    {label('readiness.reason', item.reason)}: {item.detail}
                  </span>
                </li>
              ))}
            </ul>
            {stranded.length > 30 && (
              <p className="text-muted-foreground mt-1 text-xs">
                {t('readiness.truncated', { more: stranded.length - 30 })}
              </p>
            )}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
